import { DotOutline } from "@phosphor-icons/react";
import { useMemo } from "react";

import RenderIf from "common/components/RenderIf";
import Checkbox from "common/components/ui/Checkbox";
import {
  DropdownMenuCheckbox,
  DropdownMenuItem,
} from "common/components/ui/DropdownMenu";
import ProfileImage from "common/components/ui/ProfileImage";
import { CAMPAIGN_STATES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { CampaignState } from "common/types";

import { CAMPAIGNS_FILTERS } from "../../constants";

import type { CampaignsSearchParams } from "../../types";

import LicenseIcon from "assets/icons/license.svg?react";
import RentedIcon from "assets/icons/rented.svg?react";

export default function CampaignFilterOptions<
  SearchParamsKey extends keyof CampaignsSearchParams,
>({
  searchParamsKey,
  searchParams,
  setSearchParams,
}: {
  searchParamsKey: SearchParamsKey;
  searchParams: CampaignsSearchParams;
  setSearchParams: (searchParams: CampaignsSearchParams) => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { accounts } = useSelectedWorkspaceContext();

  // Use static options or dynamic options for accounts filter
  const options = useMemo(() => {
    if (searchParamsKey === "accounts") {
      return Object.fromEntries(
        accounts.map(({ id, full_name }) => [id, full_name]),
      );
    }

    return CAMPAIGNS_FILTERS[searchParamsKey].options;
  }, [searchParamsKey, accounts]);

  function toggleParam(
    optionKey: CampaignsSearchParams[SearchParamsKey][number],
    isChecked: boolean,
  ) {
    const updatedParams = isChecked
      ? searchParams[searchParamsKey].filter(
          (selectedParam) => selectedParam !== optionKey,
        )
      : [...searchParams[searchParamsKey], optionKey];

    setSearchParams({
      ...searchParams,
      [searchParamsKey]: updatedParams,
    });
  }

  // Render custom prefix for specific filter types
  function renderOptionPrefix(optionKey: string) {
    // Status filter - show colored dot
    if (searchParamsKey === "status") {
      const { iconColor } = CAMPAIGN_STATES[optionKey as CampaignState];
      return <DotOutline size={20} weight="fill" className={iconColor} />;
    }

    // Accounts filter - show profile image with license badge
    if (searchParamsKey === "accounts") {
      const account = accounts.find(({ id }) => id === optionKey);
      if (account) {
        return (
          <div className="relative shrink-0">
            <ProfileImage size="xs" src={account.picture_url} />
            <RenderIf condition={account.type === "rental"}>
              <RentedIcon className="absolute -bottom-1 -right-[5px]" />
            </RenderIf>
            <RenderIf condition={account.type === "user" && !!account.license}>
              <LicenseIcon className="absolute -bottom-1 -right-[5px]" />
            </RenderIf>
          </div>
        );
      }
    }

    return null;
  }

  return typedObjectEntries(options).map(([optionKey, label]) => {
    const isChecked = searchParams[searchParamsKey].includes(
      optionKey as never,
    );
    const prefix = renderOptionPrefix(optionKey);

    if (isDesktop) {
      return (
        <DropdownMenuItem
          key={optionKey}
          onSelect={() =>
            setSearchParams({
              ...searchParams,
              [searchParamsKey]: [optionKey],
            })
          }
        >
          <DropdownMenuCheckbox
            isChecked={isChecked}
            onCheckedChange={() => toggleParam(optionKey, isChecked)}
          />
          {prefix}
          <span className="line-clamp-1 break-all">{label}</span>
        </DropdownMenuItem>
      );
    }

    return (
      <button
        key={optionKey}
        type="button"
        onClick={() => toggleParam(optionKey, isChecked)}
        className="flex items-center p-2 gap-x-1.5 text-button-sm"
      >
        <Checkbox isStyleOnly checked={isChecked} />
        {prefix}
        <span className="line-clamp-1 break-all">{label}</span>
      </button>
    );
  });
}
